import { useRef, useCallback, useState, useEffect } from "react";

export type SwipeDirection = "left" | "right" | null;

interface UseSwipeTabsOptions {
  tabs: string[];
  activeTab: string;
  onChangeTab: (tab: string) => void;
  onBoundarySwipe?: (direction: "left" | "right") => void;
  threshold?: number;
}

export function useSwipeTabs({ tabs, activeTab, onChangeTab, onBoundarySwipe, threshold = 50 }: UseSwipeTabsOptions) {
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const swiped = useRef(false);
  const [swipeDirection, setSwipeDirection] = useState<SwipeDirection>(null);
  const [edgeBounce, setEdgeBounce] = useState<"left" | "right" | null>(null);

  const changeTab = useCallback((newTab: string) => {
    const oldIdx = tabs.indexOf(activeTab);
    const newIdx = tabs.indexOf(newTab);
    if (newIdx === oldIdx || newIdx === -1) return;
    setSwipeDirection(newIdx > oldIdx ? "left" : "right");
    onChangeTab(newTab);
  }, [tabs, activeTab, onChangeTab]);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    swiped.current = false;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStart.current || swiped.current) return;
    const dx = e.touches[0].clientX - touchStart.current.x;
    const dy = e.touches[0].clientY - touchStart.current.y;
    if (Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy) * 1.5) {
      swiped.current = true;
      const idx = tabs.indexOf(activeTab);
      if (dx < 0 && idx < tabs.length - 1) {
        changeTab(tabs[idx + 1]);
      } else if (dx > 0 && idx > 0) {
        changeTab(tabs[idx - 1]);
      } else if (onBoundarySwipe) {
        setSwipeDirection(dx < 0 ? "left" : "right");
        onBoundarySwipe(dx < 0 ? "left" : "right");
      } else {
        // True edge — no more categories either
        setEdgeBounce(dx < 0 ? "left" : "right");
      }
    }
  }, [tabs, activeTab, changeTab, onBoundarySwipe, threshold]);

  const onTouchEnd = useCallback(() => {
    touchStart.current = null;
  }, []);

  const clearDirection = useCallback(() => setSwipeDirection(null), []);
  const clearEdgeBounce = useCallback(() => setEdgeBounce(null), []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) return;
      const idx = tabs.indexOf(activeTab);
      if (e.key === "ArrowRight") {
        e.preventDefault();
        if (idx < tabs.length - 1) changeTab(tabs[idx + 1]);
        else if (onBoundarySwipe) { setSwipeDirection("left"); onBoundarySwipe("left"); }
        else setEdgeBounce("left");
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (idx > 0) changeTab(tabs[idx - 1]);
        else if (onBoundarySwipe) { setSwipeDirection("right"); onBoundarySwipe("right"); }
        else setEdgeBounce("right");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [tabs, activeTab, changeTab, onBoundarySwipe]);

  return { onTouchStart, onTouchMove, onTouchEnd, swipeDirection, clearDirection, changeTab, edgeBounce, clearEdgeBounce };
}
